import { defineStore } from 'pinia'
import { ElMessage } from 'element-plus'
import {
  bulkUpdateQuestionBankGenerationDrafts,
  cancelQuestionBankGenerationTask,
  commitQuestionBankGenerationDrafts,
  downloadQuestionBankGenerationResult,
  generateQuestionBankFromWord,
  getQuestionBankGenerationDrafts,
  getQuestionBankGenerationTask,
  patchQuestionBankGenerationDraft,
  retryQuestionBankGenerationDrafts
} from '@/services/api'
import { normalizeDocxUploadFile } from '@/utils/docxUpload'

const TASK_STORAGE_KEY = 'question_gen_active_task'
const POSITION_STORAGE_KEY = 'question_gen_floating_position'

export const ACTIVE_TASK_STAGES = [
  'queued', 'preflighting', 'splitting', 'normalizing', 'answering',
  'reviewing', 'validating', 'committing', 'cancelling'
]
export const TERMINAL_TASK_STAGES = [
  'ready_for_manual_review', 'succeeded', 'partially_succeeded', 'failed', 'cancelled'
]
export const REVIEW_STATUSES = [
  'pending', 'agree', 'conflict', 'uncertain', 'source_conflict',
  'review_unavailable', 'failed'
]

function readJson(key, fallback) {
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? JSON.parse(raw) : fallback
  } catch (error) {
    return fallback
  }
}

function writeJson(key, value) {
  if (typeof window !== 'undefined') window.localStorage.setItem(key, JSON.stringify(value))
}

function removeItem(key) {
  if (typeof window !== 'undefined') window.localStorage.removeItem(key)
}

function optionsToText(options) {
  return (options || []).map((item) => `${item.key || ''}.${item.value || ''}`).join('\n')
}

export function normalizeDraft(item = {}) {
  const options = item.options_json || item.options || []
  return {
    ...item,
    id: item.id || item.draft_id,
    draft_id: item.draft_id || item.id,
    stem: item.stem || '',
    title: item.stem || '',
    options_json: options,
    options_text: optionsToText(options),
    final_answer: item.final_answer || '',
    answer: item.final_answer || item.proposed_answer || item.document_answer || '',
    validation_errors: item.validation_errors || item.validation_errors_json || [],
    selected: item.selected !== false,
    manual_status: item.manual_status || 'pending',
    review_status: REVIEW_STATUSES.includes(item.review_status) ? item.review_status : 'pending',
    version: Number(item.version || 1)
  }
}

export function isDraftCommitEligible(draft) {
  const answer = draft?.final_answer || draft?.proposed_answer || draft?.document_answer
  return !!draft && draft.selected !== false && draft.structure_status === 'valid' &&
    !!answer && answer !== 'unknown' && draft.manual_status === 'confirmed'
}

function stageOf(task) {
  return task?.current_stage || task?.status || ''
}

export const useQuestionGenTaskStore = defineStore('questionGenTask', {
  state: () => ({
    task: null,
    drafts: [],
    page: 1,
    pageSize: 20,
    total: 0,
    filters: { chapter: '', answer_source: '', review_status: '', manual_status: '', risk: '' },
    readonlyDrafts: true,
    recentCompletions: [],
    pollTimer: null,
    panelOpen: false,
    bubbleVisible: false,
    bubblePosition: readJson(POSITION_STORAGE_KEY, { left: 24, bottom: 24 }),
    hydrated: false,
    syncing: false,
    draftsLoading: false,
    networkError: '',
    lastError: ''
  }),

  getters: {
    taskId: (state) => state.task?.task_id || state.task?.id || '',
    status: (state) => stageOf(state.task),
    message: (state) => state.task?.summary?.message || state.task?.error_summary || '',
    inputFilename: (state) => state.task?.input_filename || '',
    filename: (state) => state.task?.filename || '题库审核稿.xlsx',
    errors: (state) => state.task?.summary?.errors || [],
    warnings: (state) => state.task?.summary?.warnings || [],
    isTracking: (state) => !!(state.task?.task_id || state.task?.id) && state.bubbleVisible,
    isRunning: (state) => ACTIVE_TASK_STAGES.includes(stageOf(state.task)),
    isTerminal: (state) => TERMINAL_TASK_STAGES.includes(stageOf(state.task)),
    canEditDrafts: (state) => ['ready_for_manual_review', 'partially_succeeded'].includes(stageOf(state.task)),
    committed: (state) => stageOf(state.task) === 'succeeded',
    generatedCount: (state) => Number(state.task?.normalized_count || state.task?.split_count || 0),
    failedCount: (state) => Number(state.task?.failed_count || 0),
    selectedDraftCount: (state) => state.drafts.filter((item) => item.selected !== false).length,
    commitEligibleCount: (state) => state.drafts.filter(isDraftCommitEligible).length,
    blockedSelectedCount: (state) => state.drafts.filter((item) => item.selected !== false && !isDraftCommitEligible(item)).length,
    selectedDraftIds: (state) => state.drafts.filter((item) => item.selected !== false).map((item) => item.draft_id),
    eligibleDraftIds: (state) => state.drafts.filter(isDraftCommitEligible).map((item) => item.draft_id),
    progress: (state) => {
      const task = state.task || {}
      const total = Number(task.estimated_total || task.split_count || 0)
      const current = Number(task.reviewed_count || task.answered_count || task.normalized_count || task.split_count || 0)
      return {
        current,
        total,
        percent: total ? Math.min(100, Math.round((current / total) * 100)) : 0,
        current_chapter: task.current_chapter || ''
      }
    }
  },

  actions: {
    hydrate() {
      if (this.hydrated) return
      this.hydrated = true
      const saved = readJson(TASK_STORAGE_KEY, null)
      if (!saved?.taskId) return
      this.task = { task_id: saved.taskId, current_stage: 'queued', status: 'queued' }
      this.panelOpen = !!saved.panelOpen
      this.bubbleVisible = true
      this.pollTask({ immediate: true })
    },

    persist() {
      if (!this.taskId) return removeItem(TASK_STORAGE_KEY)
      writeJson(TASK_STORAGE_KEY, { taskId: this.taskId, panelOpen: this.panelOpen })
    },

    setBubblePosition(position) {
      this.bubblePosition = {
        left: Math.max(8, Number(position.left || 24)),
        bottom: Math.max(8, Number(position.bottom || 24))
      }
      writeJson(POSITION_STORAGE_KEY, this.bubblePosition)
    },

    applyTask(task) {
      if (!task) return
      this.task = { ...(this.task || {}), ...task }
      this.bubbleVisible = true
      this.networkError = ''
      this.persist()
    },

    async startTask(file, options = {}) {
      if (this.isRunning) {
        this.openPanel()
        throw new Error('当前已有 Word 生成任务正在运行')
      }
      this.stopPolling()
      this.task = { task_id: '', input_filename: file?.name || '', current_stage: 'queued', status: 'queued' }
      this.drafts = []
      this.total = 0
      this.bubbleVisible = true
      this.panelOpen = true
      const formData = new FormData()
      formData.append('file', normalizeDocxUploadFile(file))
      if (options.totalCount !== undefined) formData.append('total_count', String(options.totalCount))
      if (options.requirements) formData.append('requirements', options.requirements)
      if (options.allocationMode) formData.append('allocation_mode', options.allocationMode)
      if (Array.isArray(options.chapterPlan)) formData.append('chapter_plan', JSON.stringify(options.chapterPlan))
      if (options.targetCategoryId) formData.append('target_category_id', String(options.targetCategoryId))
      const response = await generateQuestionBankFromWord(formData)
      this.trackSubmittedTask(response.data?.task || { task_id: response.data?.task_id, current_stage: 'queued' }, { openPanel: true })
      return response
    },

    trackSubmittedTask(task, { openPanel = false } = {}) {
      if (!task?.task_id && !task?.id) return
      this.applyTask(task)
      if (openPanel) this.panelOpen = true
      this.persist()
      this.loadDrafts({ silent: true })
      this.schedulePoll(500)
    },

    schedulePoll(delay = 1800) {
      this.stopPolling()
      if (!this.taskId || !this.isRunning) return
      this.pollTimer = window.setTimeout(() => this.pollTask(), delay)
    },

    stopPolling() {
      if (!this.pollTimer) return
      window.clearTimeout(this.pollTimer)
      this.pollTimer = null
    },

    async pollTask({ immediate = false } = {}) {
      if (!this.taskId) return
      this.stopPolling()
      this.syncing = true
      try {
        const previousUpdatedAt = this.task?.updated_at
        const previousCount = Number(this.task?.split_count || 0)
        const response = await getQuestionBankGenerationTask(this.taskId)
        const task = response.data?.task
        this.applyTask(task)
        if (immediate || task?.updated_at !== previousUpdatedAt || Number(task?.split_count || 0) !== previousCount) {
          await this.loadDrafts({ silent: true })
        }
        if (this.isRunning) this.schedulePoll()
      } catch (error) {
        const status = error.response?.status
        const message = error.response?.data?.message || error.message || '查询任务状态失败'
        this.lastError = message
        this.networkError = message
        if ([401, 403, 404].includes(status)) this.clearTask()
        else this.schedulePoll(5000)
      } finally {
        this.syncing = false
      }
    },

    draftQuery() {
      return {
        page: this.page,
        page_size: this.pageSize,
        ...Object.fromEntries(Object.entries(this.filters).filter(([, value]) => value !== ''))
      }
    },

    async loadDrafts({ silent = false } = {}) {
      if (!this.taskId) return
      this.draftsLoading = true
      try {
        const previous = new Map(this.drafts.map((item) => [item.draft_id, item]))
        const response = await getQuestionBankGenerationDrafts(this.taskId, this.draftQuery())
        this.drafts = (response.data?.items || response.data?.drafts || []).map(normalizeDraft)
        this.total = Number(response.data?.total || 0)
        this.page = Number(response.data?.page || this.page)
        this.pageSize = Number(response.data?.page_size || this.pageSize)
        this.readonlyDrafts = !!response.data?.readonly
        this.recentCompletions = this.drafts.filter((item) => {
          const old = previous.get(item.draft_id)
          return old && old.review_status !== item.review_status && item.review_status !== 'pending'
        }).slice(-5)
        this.applyTask(response.data?.task)
      } catch (error) {
        if (!silent) ElMessage.error('加载生成草稿失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.draftsLoading = false
      }
    },

    async setPage(page) {
      this.page = Number(page || 1)
      await this.loadDrafts()
    },

    async setPageSize(pageSize) {
      this.pageSize = Number(pageSize || 20)
      this.page = 1
      await this.loadDrafts()
    },

    async setFilters(filters) {
      this.filters = { ...this.filters, ...filters }
      this.page = 1
      await this.loadDrafts()
    },

    setDraftAnswer(draft, value) {
      if (!draft?.draft_id) return
      const current = this.drafts.find((item) => item.draft_id === draft.draft_id)
      if (!current) return
      current.final_answer = String(value ?? '')
      current.manual_status = 'pending'
    },

    async confirmDraft(draft) {
      if (!draft?.draft_id) return false
      const candidate = String(
        draft.final_answer || draft.proposed_answer || draft.document_answer || ''
      ).trim()
      if (!candidate || candidate === 'unknown') {
        ElMessage.warning('请先填写最终答案')
        return false
      }
      const confirmed = await this.patchDraft(draft, {
        final_answer: candidate,
        manual_status: 'confirmed'
      })
      if (confirmed) ElMessage.success('人工答案已确认')
      return confirmed
    },

    async confirmDrafts(drafts) {
      const rows = [...new Map(
        (drafts || []).filter((draft) => draft?.draft_id).map((draft) => [draft.draft_id, draft])
      ).values()]
      let confirmedCount = 0
      let missingCount = 0
      for (const draft of rows) {
        const candidate = String(
          draft.final_answer || draft.proposed_answer || draft.document_answer || ''
        ).trim()
        if (!candidate || candidate === 'unknown') {
          missingCount += 1
          continue
        }
        const confirmed = await this.patchDraft(draft, {
          final_answer: candidate,
          manual_status: 'confirmed'
        })
        if (confirmed) confirmedCount += 1
      }
      if (confirmedCount) ElMessage.success(`已确认 ${confirmedCount} 道题`)
      if (missingCount) ElMessage.warning(`${missingCount} 道题缺少最终答案，未确认`)
      return confirmedCount === rows.length
    },

    async patchDraft(draft, changes) {
      if (!draft?.draft_id) return false
      try {
        const response = await patchQuestionBankGenerationDraft(this.taskId, draft.draft_id, {
          ...changes,
          version: draft.version
        })
        const updated = normalizeDraft(response.data?.draft)
        const index = this.drafts.findIndex((item) => item.draft_id === updated.draft_id)
        if (index >= 0) this.drafts.splice(index, 1, updated)
        return true
      } catch (error) {
        if (error.response?.status === 409 && error.response?.data?.current) {
          const current = normalizeDraft(error.response.data.current)
          const index = this.drafts.findIndex((item) => item.draft_id === current.draft_id)
          if (index >= 0) this.drafts.splice(index, 1, current)
        }
        ElMessage.error(error.response?.data?.message || error.message || '保存题目失败')
        return false
      }
    },

    async bulkAction(action, draftIds) {
      const ids = [...new Set((draftIds || []).filter(Boolean))]
      if (!ids.length) return false
      try {
        const response = await bulkUpdateQuestionBankGenerationDrafts(this.taskId, action, ids)
        await this.loadDrafts({ silent: true })
        ElMessage.success(response.data?.message || '批量操作已完成')
        return true
      } catch (error) {
        ElMessage.error(error.response?.data?.message || error.message || '批量操作失败')
        return false
      }
    },

    async retryDrafts(stage, draftIds) {
      try {
        const response = await retryQuestionBankGenerationDrafts(this.taskId, stage, draftIds)
        this.applyTask(response.data?.task)
        await this.loadDrafts({ silent: true })
        this.schedulePoll(500)
        return true
      } catch (error) {
        ElMessage.error(error.response?.data?.message || error.message || '重试失败')
        return false
      }
    },

    async cancelTask() {
      if (!this.taskId) return
      try {
        const response = await cancelQuestionBankGenerationTask(this.taskId)
        this.applyTask(response.data?.task)
        if (this.isRunning) this.schedulePoll(1000)
      } catch (error) {
        ElMessage.error('结束任务失败: ' + (error.response?.data?.message || error.message))
      }
    },

    async commitDrafts() {
      if (!this.taskId || !this.eligibleDraftIds.length || this.blockedSelectedCount) return false
      try {
        const response = await commitQuestionBankGenerationDrafts(this.taskId, this.eligibleDraftIds)
        this.applyTask(response.data?.task)
        await this.loadDrafts({ silent: true })
        if (typeof window !== 'undefined') window.dispatchEvent(new Event('question-bank-generation-committed'))
        return true
      } catch (error) {
        const data = error.response?.data
        const details = Array.isArray(data?.errors)
          ? data.errors.slice(0, 3).map((item) => item.error || item).join('；')
          : ''
        ElMessage.error([data?.message || error.message || '入库失败', details].filter(Boolean).join('：'))
        return false
      }
    },

    async downloadExcel() {
      if (!this.taskId) return
      try {
        const response = await downloadQuestionBankGenerationResult(this.taskId)
        const blob = response.data
        if (!(blob instanceof Blob) || blob.size === 0) throw new Error('未收到有效文件')
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = this.filename
        link.click()
        URL.revokeObjectURL(url)
      } catch (error) {
        ElMessage.error('下载失败: ' + (error.response?.data?.message || error.message))
      }
    },

    openPanel() {
      this.panelOpen = true
      this.persist()
      if (this.taskId) this.pollTask({ immediate: true })
    },

    closePanel() {
      this.panelOpen = false
      this.persist()
    },

    clearTask() {
      this.stopPolling()
      this.task = null
      this.drafts = []
      this.total = 0
      this.page = 1
      this.readonlyDrafts = true
      this.panelOpen = false
      this.bubbleVisible = false
      this.networkError = ''
      this.lastError = ''
      removeItem(TASK_STORAGE_KEY)
    }
  }
})
